import { useEffect, useState } from 'react';
import { detect, fromNavigator } from "@lingui/detect-locale";
import { Wrench, Server, ArrowRight, ExternalLink } from 'lucide-react';
import { Button } from '../../components/ui/button';

type SupportedLang = 'ko' | 'ja' | 'en';

interface MaintenanceContent {
    title: string;
    subtitle: string;
    description: string[];
    devAppNotice: string;
    devAppLink: string;
    thankYou: string;
}

const content: Record<SupportedLang, MaintenanceContent> = {
    ko: {
        title: '시스템 점검 중',
        subtitle: '데이터 이전 작업 안내',
        description: [
            'SpaceChat의 데이터를 NEXTPOD로 이전하는 작업을 진행 중입니다.',
            '서비스 이용에 불편을 드려 죄송합니다.',
        ],
        devAppNotice: 'NEXTPOD의 기존 데이터는 아래 주소로 이전되었습니다:',
        devAppLink: '개발 서버로 이동',
        thankYou: '감사합니다.',
    },
    ja: {
        title: 'システムメンテナンス中',
        subtitle: 'データ移行作業のお知らせ',
        description: [
            'SpaceChatのデータをNEXTPODへ移行する作業を実施中です。',
            'ご不便をおかけして申し訳ございません。',
        ],
        devAppNotice: 'NEXTPODの既存データは以下のURLに移行されました：',
        devAppLink: '開発サーバーへ移動',
        thankYou: 'ありがとうございます。',
    },
    en: {
        title: 'System Maintenance',
        subtitle: 'Data Migration in Progress',
        description: [
            'We are currently migrating SpaceChat data to NEXTPOD.',
            'We apologize for any inconvenience.',
        ],
        devAppNotice: 'Existing NEXTPOD data has been moved to:',
        devAppLink: 'Go to Development Server',
        thankYou: 'Thank you for your patience.',
    },
};

const DEV_APP_URL = 'https://devapp.nextpod.jp';

const MaintenancePage: React.FC = () => {
    const [lang, setLang] = useState<SupportedLang>('en');

    useEffect(() => {
        const browserLang = detect(fromNavigator(), () => 'en');
        const detected = browserLang?.substring(0, 2) as SupportedLang;
        if (['ko', 'ja', 'en'].includes(detected)) {
            setLang(detected);
        }
    }, []);

    const c = content[lang];

    const handleGoToDevApp = () => {
        window.open(DEV_APP_URL, '_blank');
    };

    return (
        <div className="min-h-screen w-full flex flex-col items-center justify-center bg-gradient-to-br from-slate-900 via-slate-800 to-slate-900 text-white relative overflow-hidden font-sans selection:bg-amber-500/30">
            {/* Animated Background Effects */}
            <div className="absolute inset-0 z-0 overflow-hidden">
                {/* Gradient Orbs */}
                <div className="absolute top-[-20%] left-[-10%] w-[60%] h-[60%] bg-amber-600/10 blur-[120px] rounded-full animate-pulse"></div>
                <div className="absolute bottom-[-20%] right-[-10%] w-[50%] h-[50%] bg-blue-600/10 blur-[100px] rounded-full animate-pulse delay-700"></div>
                <div className="absolute top-[50%] left-[50%] -translate-x-1/2 -translate-y-1/2 w-[30%] h-[30%] bg-orange-500/5 blur-[80px] rounded-full"></div>

                {/* Subtle Grid Pattern */}
                <div
                    className="absolute inset-0 opacity-[0.02]"
                    style={{
                        backgroundImage: `linear-gradient(rgba(255,255,255,0.1) 1px, transparent 1px),
                            linear-gradient(90deg, rgba(255,255,255,0.1) 1px, transparent 1px)`,
                        backgroundSize: '60px 60px'
                    }}
                ></div>

                {/* Floating Particles */}
                <div className="absolute h-2 w-2 bg-amber-400/60 rounded-full top-[10%] left-[20%] animate-ping"></div>
                <div className="absolute h-1.5 w-1.5 bg-orange-400/50 rounded-full top-[30%] right-[15%] animate-ping delay-500"></div>
                <div className="absolute h-1 w-1 bg-yellow-400/40 rounded-full bottom-[20%] left-[30%] animate-ping delay-1000"></div>
            </div>

            {/* Main Content */}
            <div className="z-10 flex flex-col items-center text-center p-6 md:p-8 max-w-2xl w-full">
                {/* Status Badge */}
                <div className="inline-flex items-center gap-2.5 px-4 py-2 rounded-full bg-amber-500/10 border border-amber-500/30 backdrop-blur-sm mb-8">
                    <span className="relative flex h-3 w-3">
                        <span className="animate-ping absolute inline-flex h-full w-full rounded-full bg-amber-400 opacity-75"></span>
                        <span className="relative inline-flex rounded-full h-3 w-3 bg-amber-500"></span>
                    </span>
                    <span className="text-sm font-semibold tracking-wide text-amber-300">MAINTENANCE</span>
                </div>

                {/* Icon */}
                <div className="relative mb-6">
                    <div className="absolute inset-0 bg-amber-500/20 blur-2xl rounded-full"></div>
                    <div className="relative flex items-center justify-center w-24 h-24 rounded-2xl bg-gradient-to-br from-amber-500 to-orange-600 shadow-lg shadow-amber-500/30">
                        <Wrench className="w-12 h-12 text-white animate-pulse" />
                    </div>
                </div>

                {/* Title */}
                <h1 className="text-3xl md:text-4xl font-black mb-3 tracking-tight text-transparent bg-clip-text bg-gradient-to-r from-amber-200 via-orange-200 to-yellow-100">
                    {c.title}
                </h1>

                <h2 className="text-lg md:text-xl font-medium mb-6 text-amber-200/80">
                    {c.subtitle}
                </h2>

                {/* Description */}
                <div className="space-y-3 mb-8 max-w-md">
                    {c.description.map((text, idx) => (
                        <p key={idx} className="text-slate-300 text-base leading-relaxed">
                            {text}
                        </p>
                    ))}
                </div>

                {/* Dev App Notice Card */}
                <div className="w-full max-w-md p-5 rounded-2xl bg-slate-800/50 border border-slate-700/50 backdrop-blur-sm mb-8">
                    <div className="flex items-center justify-center gap-2 mb-3 text-slate-400">
                        <Server className="w-4 h-4" />
                        <span className="text-sm">{c.devAppNotice}</span>
                    </div>

                    <div className="flex items-center justify-center gap-2 text-blue-400 font-mono text-sm mb-4 break-all">
                        <a href={DEV_APP_URL} target="_blank" rel="noopener noreferrer" className="hover:underline">
                            {DEV_APP_URL}
                        </a>
                    </div>

                    <Button
                        onClick={handleGoToDevApp}
                        className="w-full h-12 rounded-xl bg-gradient-to-r from-blue-600 to-indigo-600 hover:from-blue-500 hover:to-indigo-500 text-white font-semibold transition-all duration-300 hover:scale-[1.02] active:scale-[0.98] shadow-lg shadow-blue-500/20 gap-2"
                    >
                        {c.devAppLink}
                        <ExternalLink className="w-4 h-4" />
                    </Button>
                </div>

                {/* Thank You */}
                <p className="text-slate-400 text-sm italic">
                    {c.thankYou}
                </p>

                {/* Language Selector */}
                <div className="flex items-center gap-2 mt-8 text-xs text-slate-500">
                    {(['ko', 'ja', 'en'] as SupportedLang[]).map((l) => (
                        <button
                            key={l}
                            onClick={() => setLang(l)}
                            className={`px-3 py-1.5 rounded-lg transition-all ${lang === l
                                    ? 'bg-amber-500/20 text-amber-300 font-medium'
                                    : 'hover:bg-slate-700/50 text-slate-500 hover:text-slate-400'
                                }`}
                        >
                            {l === 'ko' ? '한국어' : l === 'ja' ? '日本語' : 'English'}
                        </button>
                    ))}
                </div>
            </div>

            {/* Decorative Elements */}
            <div className="absolute bottom-8 left-8 opacity-30 animate-[float_6s_ease-in-out_infinite]">
                <div className="w-16 h-16 rounded-full border-2 border-dashed border-amber-500/30 flex items-center justify-center">
                    <div className="w-10 h-10 rounded-full border border-amber-500/20"></div>
                </div>
            </div>

            <div className="absolute top-12 right-12 opacity-20 animate-[float_8s_ease-in-out_infinite_reverse]">
                <ArrowRight className="w-8 h-8 text-blue-400" />
            </div>

            <style>{`
        @keyframes float {
          0%, 100% { transform: translateY(0px); }
          50% { transform: translateY(-15px); }
        }
      `}</style>
        </div>
    );
};

export default MaintenancePage;
